import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { geoNaturalEarth1, geoPath, geoCentroid } from 'd3-geo';
import { Plane, Loader2, Plus, Minus, RotateCcw } from 'lucide-react';
import { api, fmtDate } from '../api';
import MapFrame from './MapFrame';

const W = 960;
const H = 470;
const clamp = ({ k, x, y }) => ({ k, x: Math.min(0, Math.max(W - W * k, x)), y: Math.min(0, Math.max(H - H * k, y)) });
const COL = { red: '#E5484D', orange: '#E8913A', green: '#2E9E6A' };
const worst = (evs) => (evs.some((e) => e.alert === 'red') ? 'red' : evs.some((e) => e.alert === 'orange') ? 'orange' : null);
const zoneEvents = (s) => (s.city ? s.events.filter((e) => e.near) : s.events);

function StageLine({ t, s }) {
  const near = s.events.filter((e) => e.near);
  const rest = s.events.filter((e) => !e.near);
  return (
    <div className="mt-1" data-testid={`trips-map-stage-${s.id}`}>
      <p className="text-xs text-[#C9CCD1]">{s.city ? `${s.city}, ` : ''}{s.country} · {fmtDate(s.start)} – {fmtDate(s.end)}{s.active ? ' · in corso' : ''}</p>
      {!s.city && <p className="text-[11px] text-[#E8913A]" data-testid={`trips-map-city-missing-${t.id}`}>Città da indicare: eventi mostrati per tutto il Paese</p>}
      {s.city && (near.length > 0
        ? <ul className="space-y-0.5">{near.slice(0, 3).map((e) => <li key={e.id} className="text-xs truncate" style={{ color: COL[e.alert] }}>In zona · {e.type_label} · {e.title}</li>)}</ul>
        : <p className="text-xs text-[#5FC18F]">Nessun evento grave entro {s.radius_km} km</p>)}
      {rest.length > 0 && <p className="text-[11px] truncate opacity-75" style={{ color: COL[worst(rest) || 'green'] }}>Nel Paese · {rest[0].type_label} · {rest[0].title}{rest.length > 1 ? ` e altri ${rest.length - 1}` : ''}</p>}
    </div>
  );
}

function TripRow({ t }) {
  const w = worst(t.stages.flatMap(zoneEvents));
  return (
    <Link to={`/portal/travel-security/trasferte/${t.id}`} className="block px-4 py-3 hover:bg-white/[0.03]" data-testid={`trips-map-row-${t.id}`}>
      <p className="text-sm text-[#F2F2F2] flex items-center gap-2"><Plane size={13} className="text-[#D4AF37]" />{t.traveler}{t.stages.length > 1 ? ` · ${t.stages.length} tappe` : ''}</p>
      {t.stages.map((s) => <StageLine key={s.id} t={t} s={s} />)}
      {w && <span className="sr-only" data-testid={`trips-map-alert-${t.id}`}>{w}</span>}
    </Link>
  );
}

export default function TripsMap({ events }) {
  const [geo, setGeo] = useState(null);
  const [trips, setTrips] = useState(null);
  useEffect(() => {
    fetch('/geo/world.json').then((r) => r.json()).then(setGeo).catch(() => setGeo({ features: [] }));
    api.get('/travel/trips-risk').then((r) => setTrips(r.data)).catch(() => setTrips([]));
  }, []);
  const proj = useMemo(() => (geo ? geoNaturalEarth1().fitSize([W, H], geo) : null), [geo]);
  const byIso = useMemo(() => Object.fromEntries((geo?.features || []).map((f) => [f.id, f])), [geo]);
  const svgRef = useRef(null);
  const drag = useRef(null);
  const [z, setZ] = useState({ k: 1, x: 0, y: 0 });
  const zoomAt = (f, cx = W / 2, cy = H / 2) => setZ((o) => { const k = Math.min(12, Math.max(1, o.k * f)); const r = k / o.k; return clamp({ k, x: cx - (cx - o.x) * r, y: cy - (cy - o.y) * r }); });
  const toView = (e) => { const b = svgRef.current.getBoundingClientRect(); return [(e.clientX - b.left) * (W / b.width), (e.clientY - b.top) * (H / b.height)]; };
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const onWheel = (e) => { e.preventDefault(); const [cx, cy] = toView(e); zoomAt(e.deltaY < 0 ? 1.25 : 0.8, cx, cy); };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [geo, trips]); // eslint-disable-line react-hooks/exhaustive-deps
  const down = (e) => { const [px, py] = toView(e); drag.current = { px, py, x: z.x, y: z.y }; e.currentTarget.setPointerCapture(e.pointerId); };
  const move = (e) => { if (!drag.current) return; const [px, py] = toView(e); const d = drag.current; setZ((o) => clamp({ ...o, x: d.x + px - d.px, y: d.y + py - d.py })); };
  const up = () => { drag.current = null; };
  if (!geo || !trips) return <div className="min-h-[440px] flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;
  const path = geoPath(proj);
  const tripIso = new Set(trips.flatMap((t) => t.stages.map((s) => s.iso3)));
  const points = (events || []).filter((e) => e.lat != null && e.lon != null);
  const xy = (s) => (s.lat != null ? proj([s.lon, s.lat]) : byIso[s.iso3] ? proj(geoCentroid(byIso[s.iso3])) : null);
  const zb = 'h-9 w-9 grid place-items-center rounded-sm bg-[#0B0D10] border border-[#2A2F36] text-[#E5E5E5] hover:border-[#D4AF37] hover:text-[#D4AF37]';
  const tools = (
    <>
      <button className={zb} onClick={() => zoomAt(1.5)} aria-label="Ingrandisci" data-testid="trips-map-zoom-in"><Plus size={15} /></button>
      <button className={zb} onClick={() => zoomAt(1 / 1.5)} aria-label="Riduci" data-testid="trips-map-zoom-out"><Minus size={15} /></button>
      <button className={zb} onClick={() => setZ({ k: 1, x: 0, y: 0 })} aria-label="Vista iniziale" data-testid="trips-map-zoom-reset"><RotateCcw size={14} /></button>
    </>
  );
  const k = z.k;
  return (
    <div data-testid="trips-map">
      <MapFrame testid="trips-map-frame" tools={tools}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        className={`trips-svg w-full h-auto bg-[#050505] block touch-none select-none ${k > 1 ? 'cursor-grab active:cursor-grabbing' : ''}`} data-testid="trips-map-svg" data-zoom={k.toFixed(2)}>
        <g transform={`translate(${z.x},${z.y}) scale(${k})`}>
        {geo.features.map((f) => <path key={f.id || f.properties.name} d={path(f)} fill={tripIso.has(f.id) ? '#3A3215' : '#161A20'} stroke="#2A2F36" strokeWidth={0.4} vectorEffect="non-scaling-stroke" />)}
        {points.map((e) => { const [x, y] = proj([e.lon, e.lat]); return <circle key={e.id} cx={x} cy={y} r={(e.alert === 'red' ? 5 : 3.5) / Math.sqrt(k)} fill={COL[e.alert]} fillOpacity={0.75}><title>{`${e.type_label} · ${e.title}`}</title></circle>; })}
        {trips.map((t) => {
          const pts = t.stages.map(xy).filter(Boolean);
          return pts.length > 1 && <polyline key={`l-${t.id}`} points={pts.map((p) => p.join(',')).join(' ')} fill="none" stroke="#D4AF37" strokeWidth={1.2} vectorEffect="non-scaling-stroke" strokeDasharray="4 3" opacity={0.7} data-testid={`trips-map-route-${t.id}`} />;
        })}
        {trips.flatMap((t) => t.stages.map((s, i) => {
          const p = xy(s);
          if (!p) return null;
          const w = worst(zoneEvents(s));
          return (
            <g key={`${t.id}-${s.id}`} transform={`translate(${p[0]},${p[1]}) scale(${1 / k})`} data-testid={i === 0 ? `trips-map-marker-${t.id}` : `trips-map-marker-${t.id}-${i + 1}`}>
              {w && <circle r={13} fill="none" stroke={COL[w]} strokeWidth={2}><animate attributeName="r" values="9;16;9" dur="2s" repeatCount="indefinite" /></circle>}
              <circle r={6} fill="#D4AF37" stroke="#050505" strokeWidth={1.5} />
              <title>{`${t.traveler} · ${s.city ? `${s.city}, ` : ''}${s.country} (${s.start} – ${s.end})`}</title>
            </g>
          );
        }))}
        </g>
      </svg>
      </MapFrame>
      <div className="border-t border-[#1F242C] max-h-[420px] overflow-y-auto divide-y divide-[#1F242C] min-w-0 grid grid-cols-1 lg:grid-cols-2 lg:divide-y-0" data-testid="trips-map-list">
        {trips.length === 0 ? <p className="p-4 text-sm text-[#5A5F67]" data-testid="trips-map-empty">Nessuna trasferta in corso o futura. Registrala in «Compliance e trasferte».</p>
          : trips.map((t) => <TripRow key={t.id} t={t} />)}
      </div>
    </div>
  );
}
